import React from 'react';
import type { VendorPortalWorkGroupTableStatus } from '../../utils/vendorPortalApprovals';
import { sidebarTableStatusLabel } from '../../utils/vendorPortalApprovals';

export interface VendorPortalSidebarStatusTableProps {
  timberStatus: VendorPortalWorkGroupTableStatus | null;
  cncStatus: VendorPortalWorkGroupTableStatus | null;
  showTimber: boolean;
  showCnc: boolean;
}

export interface VendorPortalSidebarDealContentProps {
  projectNumber: string | null;
  sidebarTitle: string;
  materialsBidTotal: number | null;
  timberStatus: VendorPortalWorkGroupTableStatus | null;
  cncStatus: VendorPortalWorkGroupTableStatus | null;
  showTimber: boolean;
  showCnc: boolean;
  formatBidTotal: (total: number) => string;
  /** Soonest open quote deadline from a Files → RFQs request round, if any. */
  quoteDueDate?: Date | null;
}

function StatusHeaders({ showTimber, showCnc }: Pick<VendorPortalSidebarStatusTableProps, 'showTimber' | 'showCnc'>) {
  if (!showTimber && !showCnc) return null;
  return (
    <div className="vp-sidebar-status-headers" aria-hidden>
      {showTimber ? <span className="vp-sidebar-status-col-timber">Timber</span> : null}
      {showCnc ? <span className="vp-sidebar-status-col-cnc">CNC</span> : null}
    </div>
  );
}

function StatusValues({
  timberStatus,
  cncStatus,
  showTimber,
  showCnc,
}: VendorPortalSidebarStatusTableProps) {
  if (!showTimber && !showCnc) return null;

  const renderCell = (status: VendorPortalWorkGroupTableStatus | null) =>
    status ? (
      <span className={`vp-sidebar-status-value vp-sidebar-status-value--${status}`}>
        {sidebarTableStatusLabel(status)}
      </span>
    ) : (
      <span className="vp-sidebar-status-value vp-sidebar-status-value--empty">—</span>
    );

  return (
    <div className="vp-sidebar-status-values">
      {showTimber ? (
        <span className="vp-sidebar-status-col-timber">{renderCell(timberStatus)}</span>
      ) : null}
      {showCnc ? (
        <span className="vp-sidebar-status-col-cnc">{renderCell(cncStatus)}</span>
      ) : null}
    </div>
  );
}

const VendorPortalSidebarStatusTable: React.FC<VendorPortalSidebarStatusTableProps> = props => (
  <>
    <StatusHeaders showTimber={props.showTimber} showCnc={props.showCnc} />
    <StatusValues {...props} />
  </>
);

export const VendorPortalSidebarDealContent: React.FC<VendorPortalSidebarDealContentProps> = ({
  projectNumber,
  sidebarTitle,
  materialsBidTotal,
  timberStatus,
  cncStatus,
  showTimber,
  showCnc,
  formatBidTotal,
  quoteDueDate = null,
}) => (
  <div className="vp-sidebar-item-layout" aria-label="Quote status">
    {projectNumber && <span className="vp-sidebar-project-number">{projectNumber}</span>}
    <span className="vp-sidebar-item-descriptor">
      {sidebarTitle}
      {quoteDueDate ? (
        <span
          className={`vp-sidebar-due${quoteDueDate.getTime() < Date.now() ? ' vp-sidebar-due--overdue' : ''}`}
          title={`Quotes due ${quoteDueDate.toLocaleString()}`}
        >
          Due {quoteDueDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </span>
      ) : null}
    </span>
    <StatusHeaders showTimber={showTimber} showCnc={showCnc} />
    {materialsBidTotal != null ? (
      <span className="vp-sidebar-bid-total">{formatBidTotal(materialsBidTotal)}</span>
    ) : (
      <span className="vp-sidebar-bid-total vp-sidebar-bid-total--empty" aria-hidden />
    )}
    <StatusValues
      timberStatus={timberStatus}
      cncStatus={cncStatus}
      showTimber={showTimber}
      showCnc={showCnc}
    />
  </div>
);

export default VendorPortalSidebarStatusTable;
